import { useMemo } from 'react'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { useMessagesQuery } from '@/hooks/useMessagesQuery'
import type { MessageRecord } from '../types'
import { messagesColumns } from './MessagesColumns'
import {
  messageFiltersToQuery,
  messagesFilterFields,
  messagesSchema,
} from './messagesTableSchema'
import { MessageLogsSheetWrapper } from './MessageThreadSheet'

const TABLE_ID = 'messages'
const getRowId = (row: MessageRecord) => row.id

function MessagesListInner() {
  const state = useFilterState()
  const { resetAllFilters } = useFilterActions()
  const params = messageFiltersToQuery(state)
  const query = useMessagesQuery(params)
  const data = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data]
  )

  return (
    <DataTableInfinite<MessageRecord, unknown>
      tableId={TABLE_ID}
      autoColumnWidth
      columns={messagesColumns}
      data={data}
      filterFields={messagesFilterFields}
      manualFiltering
      manualSorting
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error}
      entityLabel="messages"
      onClearFilters={resetAllFilters}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={query.refetch}
      totalRowsFetched={data.length}
      getRowId={getRowId}
      dataUpdatedAt={query.dataUpdatedAt}
      sheetSlot={<MessageLogsSheetWrapper />}
      toolbarActions={
        <TabRefreshButton
          onRefresh={query.refetch}
          isFetching={query.isFetching}
        />
      }
      flexColumnId="body"
    />
  )
}

export function MessagesList() {
  const adapter = useNuqsAdapter(messagesSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
  })
  return (
    <DataTableStoreProvider adapter={adapter}>
      <MessagesListInner />
    </DataTableStoreProvider>
  )
}
